/**
 * React Query wrappers around the API client.
 */
import { useCallback, useEffect, useState } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query'
import { api, ApiError } from '@/api/client'
import type {
  Coverage,
  DialogDetail,
  DialogSummary,
  DialogTurn,
  ImageSearchResult,
  SecondLook,
  Payment,
  StarsInvoice,
  PaymentStatus,
  PhotoPlace,
  Profile,
  SearchResult,
  Tariff,
  TelegramLoginConfig,
} from '@/api/types'

export const queryKeys = {
  me: ['me'] as const,
  dialogs: ['dialogs'] as const,
  dialog: (id: number) => ['dialog', id] as const,
  tariffs: ['tariffs'] as const,
  payment: (orderId: string) => ['payment', orderId] as const,
  coverage: ['coverage'] as const,
  telegramLogin: ['telegram-login'] as const,
  nearby: (lat: number, lon: number, radius: number) => ['nearby', lat, lon, radius] as const,
  search: (query: string, log: boolean) => ['search', query, log] as const,
}

/** Retrying a business error (a spent limit, a bad code) only wastes time. */
function retryUnlessDomainError(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiError && error.status < 500) return false
  return failureCount < 2
}

export function useProfile(enabled = true) {
  return useQuery<Profile, ApiError>({
    queryKey: queryKeys.me,
    queryFn: () => api.me(),
    enabled,
    retry: retryUnlessDomainError,
  })
}

/** Changes only with a deploy, so one request per visit is enough. */
export function useTelegramLoginConfig(enabled = true) {
  return useQuery<TelegramLoginConfig, ApiError>({
    queryKey: queryKeys.telegramLogin,
    queryFn: () => api.telegramLoginConfig(),
    enabled,
    staleTime: Infinity,
    retry: retryUnlessDomainError,
  })
}

/** The list moves a few times a day, so it is kept for the whole session. */
export function useCoverage() {
  return useQuery<Coverage, ApiError>({
    queryKey: queryKeys.coverage,
    queryFn: () => api.coverage(),
    staleTime: 60 * 60 * 1000,
    retry: retryUnlessDomainError,
  })
}

export function useNearby(
  coords: { lat: number; lon: number } | null,
  radius: number,
  options?: Partial<UseQueryOptions<SearchResult, ApiError>>,
) {
  return useQuery<SearchResult, ApiError>({
    queryKey: coords ? queryKeys.nearby(coords.lat, coords.lon, radius) : ['nearby', 'idle'],
    queryFn: () => api.nearby(coords!.lat, coords!.lon, radius),
    enabled: Boolean(coords),
    retry: retryUnlessDomainError,
    ...options,
  })
}

/** log=false while typing; the previous results stay on screen until the next ones arrive. */
export function useNameSearch(query: string, log: boolean) {
  return useQuery<SearchResult, ApiError>({
    queryKey: queryKeys.search(query, log),
    queryFn: () => api.searchByName(query, log),
    enabled: query.trim().length >= 3,
    placeholderData: keepPreviousData,
    retry: retryUnlessDomainError,
  })
}

/** The logged search for a query, reusing it when the user already submitted the same text. */
export function useLoggedNameSearch() {
  const queryClient = useQueryClient()
  return (query: string) =>
    queryClient.ensureQueryData<SearchResult>({
      queryKey: queryKeys.search(query, true),
      queryFn: () => api.searchByName(query, true),
    })
}

export function useImageSearch() {
  return useMutation<ImageSearchResult, ApiError, { file: Blob; place: PhotoPlace | null }>({
    mutationFn: ({ file, place }) => api.searchByImage(file, place),
  })
}

export function useSecondLook() {
  return useMutation<SecondLook, ApiError, { file: Blob; activityId: number | null; shownIds: number[] }>({
    mutationFn: ({ file, activityId, shownIds }) => api.secondLook(file, activityId, shownIds),
  })
}

export function useDescribePhoto() {
  const queryClient = useQueryClient()
  return useMutation<DialogTurn, ApiError, { file: Blob; activityId: number | null; name: string | null }>({
    mutationFn: ({ file, activityId, name }) => api.describePhoto(file, activityId, name),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.dialogs })
      void queryClient.invalidateQueries({ queryKey: queryKeys.me })
    },
  })
}

export function useStartDialog() {
  const queryClient = useQueryClient()
  return useMutation<DialogTurn, ApiError, { attractionId: number; source: string; activityId?: number | null }>({
    mutationFn: ({ attractionId, source, activityId }) => api.startDialog(attractionId, source, activityId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.dialogs })
      void queryClient.invalidateQueries({ queryKey: queryKeys.me })
    },
  })
}

export function useAsk(dialogId: number) {
  const queryClient = useQueryClient()
  return useMutation<DialogTurn, ApiError, { question?: string; index?: number }>({
    mutationFn: ({ question, index }) =>
      index === undefined ? api.ask(dialogId, question ?? '') : api.askFollowUp(dialogId, index),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.dialog(dialogId) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.me })
    },
  })
}

export function useDialogs() {
  return useQuery<DialogSummary[], ApiError>({
    queryKey: queryKeys.dialogs,
    queryFn: () => api.dialogs(),
    retry: retryUnlessDomainError,
  })
}

export function useDialog(dialogId: number) {
  return useQuery<DialogDetail, ApiError>({
    queryKey: queryKeys.dialog(dialogId),
    queryFn: () => api.dialog(dialogId),
    enabled: Number.isFinite(dialogId),
    retry: retryUnlessDomainError,
  })
}

export function useTariffs() {
  return useQuery<Tariff[], ApiError>({
    queryKey: queryKeys.tariffs,
    queryFn: () => api.tariffs(),
    retry: retryUnlessDomainError,
  })
}

export function useCreatePayment() {
  return useMutation<Payment, ApiError, { tariffDays: number; email?: string; phone?: string }>({
    mutationFn: ({ tariffDays, email, phone }) => api.createPayment(tariffDays, email, phone),
  })
}

export function useCreateStarsInvoice() {
  return useMutation<StarsInvoice, ApiError, { tariffDays: number; test?: boolean }>({
    mutationFn: ({ tariffDays, test }) => api.createStarsInvoice(tariffDays, test),
  })
}

/**
 * Polls while the payment is still pending, for stopAfterMs at most; a paid
 * or failed payment stops it at once.
 *
 * `expired` turns true once the window is over without an outcome, and only
 * then; between two polls the payment is still being waited for. `restart`
 * opens a new window, for the "Check again" button.
 */
export function usePaymentStatus(orderId: string | null, pollMs = 3000, stopAfterMs = 60000) {
  // Kept across renders: a fresh Date.now() on every render never let the window end
  const [startedAt, setStartedAt] = useState(() => Date.now())
  const [expired, setExpired] = useState(false)

  useEffect(() => {
    setExpired(false)
    const timer = setTimeout(() => setExpired(true), stopAfterMs)
    return () => clearTimeout(timer)
  }, [startedAt, stopAfterMs])

  const query = useQuery<PaymentStatus, ApiError>({
    queryKey: orderId ? queryKeys.payment(orderId) : ['payment', 'idle'],
    queryFn: () => api.paymentStatus(orderId!),
    enabled: Boolean(orderId),
    retry: retryUnlessDomainError,
    refetchInterval: (state) => {
      if (state.state.data?.paid || state.state.data?.failed) return false
      if (Date.now() - startedAt > stopAfterMs) return false
      return pollMs
    },
  })

  const restart = useCallback(() => {
    setStartedAt(Date.now())
    void query.refetch()
  }, [query])

  return { ...query, expired: expired && !query.data?.paid && !query.data?.failed, restart }
}
